import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
    getFlightOffers,
    FlightOfferOption,
    FlightOffersResult,
    getFlightOptionsFromBackend,
    getAlternativeFlightsFromBackend,
    AlternativeFlightOption
} from '../services/tourvisioService';
import type { ApiLogEntry } from '../types';
import { useLocalization } from '../hooks/useLocalization';

interface FlightSelectionProps {
    logApiCall: (log: ApiLogEntry) => void;
}

const FlightCard: React.FC<{
    flight: FlightOfferOption;
    isSelected: boolean;
    onSelect: () => void;
}> = ({ flight, isSelected, onSelect }) => (
    <div
        className={`flight-card ${isSelected ? 'selected' : ''}`}
        onClick={onSelect}
        style={{
            border: isSelected ? '2px solid #2563eb' : '1px solid #e5e7eb',
            borderRadius: '12px',
            padding: '16px',
            marginBottom: '12px',
            cursor: 'pointer',
            background: isSelected ? '#eff6ff' : '#fff',
            transition: 'all 0.2s ease'
        }}
    >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            {/* Airline Logo & Info */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                {flight.logoUrl ? (
                    <img
                        src={flight.logoUrl}
                        alt={flight.airline}
                        style={{ width: 40, height: 40, objectFit: 'contain' }}
                        onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                    />
                ) : (
                    <div style={{ width: 40, height: 40, background: '#e5e7eb', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        ✈️
                    </div>
                )}
                <div>
                    <div style={{ fontWeight: 600, color: '#1f2937' }}>{flight.airline}</div>
                    <div style={{ fontSize: '12px', color: '#6b7280' }}>{flight.flightNumber}</div>
                </div>
            </div>

            {/* Times */}
            <div style={{ textAlign: 'center', flex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '16px' }}>
                    <div>
                        <div style={{ fontSize: '20px', fontWeight: 700, color: '#1f2937' }}>{flight.departureTime}</div>
                        <div style={{ fontSize: '12px', color: '#6b7280' }}>{flight.departureAirport}</div>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        <div style={{ fontSize: '11px', color: '#9ca3af' }}>{flight.duration}</div>
                        <div style={{ width: '80px', height: '2px', background: '#e5e7eb', position: 'relative', margin: '4px 0' }}>
                            <div style={{
                                position: 'absolute',
                                right: 0,
                                top: '-3px',
                                borderTop: '4px solid transparent',
                                borderBottom: '4px solid transparent',
                                borderLeft: '8px solid #9ca3af'
                            }}></div>
                        </div>
                        <div style={{ fontSize: '11px', color: '#9ca3af' }}>
                            {flight.stops === 0 ? 'Direct' : `${flight.stops} stop${flight.stops > 1 ? 's' : ''}`}
                        </div>
                    </div>
                    <div>
                        <div style={{ fontSize: '20px', fontWeight: 700, color: '#1f2937' }}>{flight.arrivalTime}</div>
                        <div style={{ fontSize: '12px', color: '#6b7280' }}>{flight.arrivalAirport}</div>
                    </div>
                </div>
            </div>

            {/* Price & Select */}
            <div style={{ textAlign: 'right' }}>
                {flight.priceDifference !== 0 && (
                    <div style={{
                        fontSize: '14px',
                        fontWeight: 600,
                        color: flight.priceDifference > 0 ? '#ef4444' : '#22c55e'
                    }}>
                        {flight.priceDifference > 0 ? '+' : ''}{flight.priceDifference} {flight.currency}
                    </div>
                )}
                <div style={{
                    marginTop: '8px',
                    padding: '6px 16px',
                    borderRadius: '6px',
                    background: isSelected ? '#2563eb' : '#f3f4f6',
                    color: isSelected ? '#fff' : '#4b5563',
                    fontSize: '13px',
                    fontWeight: 500
                }}>
                    {isSelected ? '✓ Selected' : 'Select'}
                </div>
            </div>
        </div>
        <div style={{ marginTop: '8px', fontSize: '12px', color: '#6b7280' }}>
            {flight.flightDate}
        </div>
    </div>
);

export const FlightSelection: React.FC<FlightSelectionProps> = ({ logApiCall }) => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { t } = useLocalization();

    const [loading, setLoading] = useState(true);
    const [flightOffers, setFlightOffers] = useState<FlightOffersResult | null>(null);
    const [selectedOutbound, setSelectedOutbound] = useState<FlightOfferOption | null>(null);
    const [selectedReturn, setSelectedReturn] = useState<FlightOfferOption | null>(null);
    const [error, setError] = useState<string | null>(null);

    // Get params from URL - support both old and new parameter names
    const offerId = searchParams.get('offerId') || '';
    const departureId = searchParams.get('departureId') || searchParams.get('departure') || '';
    const arrivalId = searchParams.get('arrivalId') || searchParams.get('arrival') || '';
    const checkIn = searchParams.get('checkIn') || searchParams.get('date') || '';
    const nights = parseInt(searchParams.get('nights') || '7');
    const adults = parseInt(searchParams.get('adults') || '2');
    const children = parseInt(searchParams.get('children') || '0');
    const childrenAges = searchParams.get('childrenAges')?.split(',').map(Number) || [];
    const hotelName = searchParams.get('hotelName') || 'Hotel';
    const roomType = searchParams.get('roomType') || 'Room';
    const totalPrice = parseFloat(searchParams.get('totalPrice') || '0');


    useEffect(() => {
        const fetchFlights = async () => {
            setLoading(true);
            setError(null);

            try {
                let result: FlightOffersResult | null = null;

                // STEP 1: Try to fetch flight options from backend (uses getPagingData + caching)
                if (offerId) {
                    console.log('🔄 Trying backend flight-options endpoint first...');
                    const backendResult = await getFlightOptionsFromBackend(offerId);

                    if (backendResult.flightOptions.length > 0) {
                        console.log(`✅ Got ${backendResult.flightOptions.length} flights from backend (cache: ${backendResult.fromCache})`);

                        // Convert AlternativeFlightOption to FlightOfferOption format
                        const outboundFlights: FlightOfferOption[] = [];
                        const returnFlights: FlightOfferOption[] = [];

                        backendResult.flightOptions.forEach((alt) => {
                            const opt: FlightOfferOption = {
                                offerId: alt.offerId,
                                flightGroupId: alt.id,
                                direction: alt.direction === 'outbound' ? 'outbound' : 'return',
                                airline: alt.airline,
                                airlineCode: '',
                                flightNumber: alt.flightNumber,
                                departureAirport: alt.departureCode || alt.departureAirport,
                                arrivalAirport: alt.arrivalCode || alt.arrivalAirport,
                                departureTime: alt.departureTime,
                                arrivalTime: alt.arrivalTime,
                                flightDate: alt.flightDate,
                                duration: alt.duration,
                                stops: alt.stops,
                                logoUrl: alt.airlineLogo,
                                priceDifference: alt.priceDifference,
                                currency: alt.currency,
                                isSelected: alt.isDefault
                            };
                            if (alt.direction === 'outbound') {
                                outboundFlights.push(opt);
                            } else {
                                returnFlights.push(opt);
                            }
                        });

                        result = {
                            outboundFlights,
                            returnFlights,
                            basePrice: totalPrice,
                            currency: 'EUR'
                        };
                    }
                }

                // STEP 2: Fallback to direct API call if backend returned no results
                if (!result || (result.outboundFlights.length === 0 && result.returnFlights.length === 0)) {
                    console.log('🔄 Backend empty, falling back to direct API call...');
                    result = await getFlightOffers(
                        offerId,
                        departureId,
                        arrivalId,
                        checkIn,
                        nights,
                        adults,
                        children,
                        childrenAges,
                        logApiCall,
                        language
                    );
                }

                setFlightOffers(result);

                // Auto-select first flights (prefer default/selected ones)
                if (result.outboundFlights.length > 0) {
                    const defaultOutbound = result.outboundFlights.find(f => f.isSelected) || result.outboundFlights[0];
                    setSelectedOutbound(defaultOutbound);
                }
                if (result.returnFlights.length > 0) {
                    const defaultReturn = result.returnFlights.find(f => f.isSelected) || result.returnFlights[0];
                    setSelectedReturn(defaultReturn);
                }

                // Check if no flights found
                if (result.outboundFlights.length === 0 && result.returnFlights.length === 0) {
                    setError('No flights found for this route. Please try different dates or destinations.');
                }
            } catch (error) {
                console.error('Failed to load flight offers:', error);
                setError('Failed to load flights. Please try again later.');
            }
            setLoading(false);
        };

        if (departureId && arrivalId && checkIn) {
            fetchFlights();
        } else if (offerId) {
            // If we have offerId but no route info, still try to fetch
            fetchFlights();
        } else {
            setLoading(false);
            setError('Missing required search parameters. Please return to the search page and try again.');
        }
    }, [offerId, departureId, arrivalId, checkIn, nights, adults, children]);


    const handleConfirm = () => {
        // Retrieve hotel and searchQuery from sessionStorage
        const storedHotel = sessionStorage.getItem('flightSelection_hotel');
        const storedSearchQuery = sessionStorage.getItem('flightSelection_searchQuery');

        const hotel = storedHotel ? JSON.parse(storedHotel) : null;
        const searchQueryData = storedSearchQuery ? JSON.parse(storedSearchQuery) : null;

        // Navigate back to hotel detail with selected flights and state
        const params = new URLSearchParams(searchParams);
        const hotelId = params.get('hotelId') || offerId; // Fallback to offerId if hotelId not found

        if (selectedOutbound) {
            params.set('selectedOutboundId', selectedOutbound.flightGroupId);
        }
        if (selectedReturn) {
            params.set('selectedReturnId', selectedReturn.flightGroupId);
        }

        // Clean up sessionStorage
        sessionStorage.removeItem('flightSelection_hotel');
        sessionStorage.removeItem('flightSelection_searchQuery');

        // Navigate with state to preserve hotel data
        navigate(`/hotel/${hotelId}?${params.toString()}`, {
            state: {
                hotel: hotel,
                searchQuery: searchQueryData,
                selectedOutboundFlight: selectedOutbound,
                selectedReturnFlight: selectedReturn
            }
        });
    };

    const calculateTotalPrice = () => {
        let price = totalPrice || flightOffers?.basePrice || 0;
        if (selectedOutbound) price += selectedOutbound.priceDifference;
        if (selectedReturn) price += selectedReturn.priceDifference;
        return price;
    };

    return (
        <div style={{
            minHeight: '100vh',
            background: 'linear-gradient(135deg, #1e3a5f 0%, #0f172a 100%)',
            padding: '24px'
        }}>
            <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
                {/* Header */}
                <div style={{
                    background: '#fff',
                    borderRadius: '16px',
                    padding: '24px',
                    marginBottom: '24px'
                }}>
                    <button
                        onClick={() => navigate(-1)}
                        style={{
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            color: '#6b7280',
                            fontSize: '14px',
                            marginBottom: '16px'
                        }}
                    >
                        ← Back to Hotel
                    </button>
                    <h1 style={{ fontSize: '24px', fontWeight: 700, color: '#1f2937', marginBottom: '8px' }}>
                        Select Your Flights
                    </h1>
                    <p style={{ color: '#6b7280' }}>
                        {hotelName} • {roomType} • {nights} nights
                    </p>
                </div>

                {error && (
                    <div style={{
                        background: '#fef2f2',
                        borderRadius: '16px',
                        padding: '24px',
                        marginBottom: '24px',
                        border: '1px solid #fecaca'
                    }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                            <div style={{ fontSize: '24px' }}>⚠️</div>
                            <div>
                                <div style={{ color: '#991b1b', fontWeight: 600, marginBottom: '4px' }}>
                                    Flight Search Error
                                </div>
                                <div style={{ color: '#dc2626' }}>{error}</div>
                            </div>
                        </div>
                        <button
                            onClick={() => navigate(-1)}
                            style={{
                                marginTop: '16px',
                                background: '#dc2626',
                                color: '#fff',
                                padding: '10px 20px',
                                borderRadius: '8px',
                                border: 'none',
                                cursor: 'pointer',
                                fontWeight: 500
                            }}
                        >
                            ← Go Back
                        </button>
                    </div>
                )}

                {loading ? (
                    <div style={{
                        background: '#fff',
                        borderRadius: '16px',
                        padding: '48px',
                        textAlign: 'center'
                    }}>
                        <div style={{ fontSize: '24px', marginBottom: '16px' }}>✈️</div>
                        <div style={{ color: '#6b7280' }}>Loading flight options...</div>
                        <div style={{ color: '#9ca3af', fontSize: '12px', marginTop: '8px' }}>
                            Searching {departureId} → {arrivalId} for {checkIn}
                        </div>
                    </div>
                ) : (
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px' }}>
                        {/* Outbound Flights */}
                        <div style={{
                            background: '#fff',
                            borderRadius: '16px',
                            padding: '24px'
                        }}>
                            <h2 style={{
                                fontSize: '18px',
                                fontWeight: 600,
                                color: '#1f2937',
                                marginBottom: '16px',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px'
                            }}>
                                🛫 Outbound Flight
                            </h2>
                            {flightOffers?.outboundFlights.length === 0 ? (
                                <div style={{ color: '#9ca3af', textAlign: 'center', padding: '24px' }}>
                                    No outbound flights available
                                </div>
                            ) : (
                                flightOffers?.outboundFlights.map((flight, idx) => (
                                    <FlightCard
                                        key={`out-${idx}`}
                                        flight={flight}
                                        isSelected={selectedOutbound?.flightGroupId === flight.flightGroupId}
                                        onSelect={() => setSelectedOutbound(flight)}
                                    />
                                ))
                            )}
                        </div>

                        {/* Return Flights */}
                        <div style={{
                            background: '#fff',
                            borderRadius: '16px',
                            padding: '24px'
                        }}>
                            <h2 style={{
                                fontSize: '18px',
                                fontWeight: 600,
                                color: '#1f2937',
                                marginBottom: '16px',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px'
                            }}>
                                🛬 Return Flight
                            </h2>
                            {flightOffers?.returnFlights.length === 0 ? (
                                <div style={{ color: '#9ca3af', textAlign: 'center', padding: '24px' }}>
                                    No return flights available
                                </div>
                            ) : (
                                flightOffers?.returnFlights.map((flight, idx) => (
                                    <FlightCard
                                        key={`ret-${idx}`}
                                        flight={flight}
                                        isSelected={selectedReturn?.flightGroupId === flight.flightGroupId}
                                        onSelect={() => setSelectedReturn(flight)}
                                    />
                                ))
                            )}
                        </div>
                    </div>
                )}

                {/* Footer / Summary */}
                {!loading && (
                    <div style={{
                        background: '#fff',
                        borderRadius: '16px',
                        padding: '24px',
                        marginTop: '24px',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                    }}>
                        <div>
                            <div style={{ fontSize: '14px', color: '#6b7280' }}>Total Price</div>
                            <div style={{ fontSize: '28px', fontWeight: 700, color: '#2563eb' }}>
                                {Math.round(calculateTotalPrice()).toLocaleString('sv-SE')} EUR
                            </div>
                        </div>
                        <button
                            onClick={handleConfirm}
                            disabled={!selectedOutbound && !selectedReturn}
                            style={{
                                background: selectedOutbound || selectedReturn ? '#2563eb' : '#9ca3af',
                                color: '#fff',
                                padding: '14px 32px',
                                borderRadius: '8px',
                                fontSize: '16px',
                                fontWeight: 600,
                                border: 'none',
                                cursor: selectedOutbound || selectedReturn ? 'pointer' : 'not-allowed'
                            }}
                        >
                            Confirm Selection →
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default FlightSelection;
